import {
  call,
  put,
  takeLatest,
  take,
  fork,
  cancel,
  select,
} from "redux-saga/effects";
import { eventChannel, EventChannel } from "redux-saga";
import { Task } from "redux-saga";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  sendMessageStart,
  sendMessageSuccess,
  sendMessageFailure,
  setConversationContext,
  messagesReceived,
  clearConversation,
} from "./agentChatSlice";
import { agentFirestoreService } from "../services/agentFirestoreService";
import { AgentConversationContext } from "../types";
import { ConversationMessage } from "@stratify/shared";

// Selectors
const selectContext = (state: any) => state.agentChat.context;

/**
 * Creates a channel for the Firestore messages subscription
 */
function createMessagesChannel(pathPrefix: string, conversationId: string) {
  return eventChannel<ConversationMessage[]>((emit) => {
    // Set up the subscription
    const unsubscribe = agentFirestoreService.subscribeToMessages(
      pathPrefix,
      conversationId,
      (messages) => {
        emit(messages);
      }
    );

    // Return unsubscribe function
    return () => {
      unsubscribe();
    };
  });
}

/**
 * Handles sending a message to the conversation
 */
function* handleSendMessage(
  action: PayloadAction<{ text: string }>
): Generator<any, void, any> {
  try {
    console.log("helllloooooooo");
    const context: AgentConversationContext | null =
      yield select(selectContext);
    if (!context) {
      throw new Error("No active conversation context");
    }

    yield call(
      agentFirestoreService.addUserMessage,
      context.pathPrefix,
      context.conversationId,
      action.payload.text
    );

    yield put(sendMessageSuccess());
  } catch (error) {
    console.error("Error sending message:", error);
    yield put(
      sendMessageFailure(
        error instanceof Error ? error.message : "Failed to send message"
      )
    );
  }
}

/**
 * Handles the messages subscription for a conversation
 */
function* handleMessagesSubscription(
  pathPrefix: string,
  conversationId: string
): Generator<any, void, any> {
  // Create the subscription channel
  const channel: EventChannel<ConversationMessage[]> = yield call(
    createMessagesChannel,
    pathPrefix,
    conversationId
  );

  try {
    // Process messages as they arrive
    while (true) {
      const messages: ConversationMessage[] = yield take(channel);
      yield put(messagesReceived(messages));
    }
  } finally {
    // Cleanup subscription when the saga is cancelled
    channel.close();
  }
}

/**
 * Watches for conversation context changes and manages the subscription
 */
function* watchConversationContext(): Generator<any, void, any> {
  let subscriptionTask: Task | null = null;

  while (true) {
    // Wait for either setConversationContext or clearConversation
    const action: PayloadAction<AgentConversationContext> | { type: string } =
      yield take([setConversationContext.type, clearConversation.type]);

    // Cancel existing subscription if there is one
    if (subscriptionTask) {
      yield cancel(subscriptionTask);
      subscriptionTask = null;
    }

    // If it's setConversationContext, start a new subscription
    if (action.type === setConversationContext.type) {
      const { pathPrefix, conversationId } = (
        action as PayloadAction<AgentConversationContext>
      ).payload;
      subscriptionTask = yield fork(
        handleMessagesSubscription,
        pathPrefix,
        conversationId
      );
    }
  }
}

/**
 * Root saga for the agent chat feature
 */
export default function* agentChatSaga(): Generator {
  yield takeLatest(sendMessageStart.type, handleSendMessage);
  yield fork(watchConversationContext);
}
